import { redirect, type MetaFunction, data, useNavigate } from "react-router";
import type { Route } from "./+types/_index";
import { Link } from "react-router";
import { useForm } from "@rvf/react";
import { z } from "zod";
import { RVFInput } from "~/components/RVF/RVFInput";
import { RVFSubmitButton } from "~/components/RVF/RVFSubmitButton";
import { RVFHiddenInput } from "~/components/RVF/RVFHiddenInput";
import { useState } from "react";
import AppStoreBranding from "~/components/AppStoreBranding";
import Welcome2 from "~/components/Welcome2";
import { hasAuth } from "~/helpers/token.server";
import axios, { isAxiosError } from "axios";
import type { User } from "~/db/schema/user";
import type { SubscriptionStatus } from "~/models/SubscriptionStatus";
import { useUserDetails } from "~/helpers/UserContext";
import { useRcStatus } from "~/helpers/RcContext";
import { toast } from "react-toastify";
import type { UserDetails } from "~/models/UserDetails";

export const meta: MetaFunction = () => {
  return [{ title: "RollCall2 - Login" }];
};

const validator = z.object({
  email: z
    .string()
    .trim()
    .min(1, { message: "Required!" })
    .email("Invalid email address"),
  subaction: z.string(),
  password: z
    .string()
    .trim()
    .min(6, { message: "Password must be at least 6 characters long" })
    .refine(
      (value) => /[a-z]/.test(value),
      "Password must contain a lowercase character",
    )
    .refine(
      (value) => /[A-Z]/.test(value),
      "Password must contain a uppercase character",
    )
    .refine(
      // eslint-disable-next-line no-useless-escape
      (value) => /[!@#\$%\^&\*]/.test(value),
      "Password must contain a special character (!@#$%^&*)",
    )
    .refine((value) => /\d/.test(value), "Password must contain a number"),
});

export const loader = async ({ request, context }: Route.LoaderArgs) => {
  const authed = await hasAuth(request, context);

  if (authed) {
    return redirect("/people");
  }
  return data({});
};

export default function Index() {
  const [userDetails, setUserDetails] = useUserDetails();
  const [subscriptionStatus, setSubscriptionStatus] = useRcStatus();
  const [creds, setCreds] = useState({ email: "", password: "" });
  const navigate = useNavigate();

  const form = useForm({
    schema: validator,
    defaultValues: {
      subaction: "login",
      email: "",
      password: "",
    },
    handleSubmit: (data, formData, options) => {
      setCreds({ email: data.email, password: data.password });
      return handleLogin(data);
    },
    onSubmitFailure: (error) => {
      form.resetForm({
        email: creds.email,
        password: creds.password,
        subaction: "login",
      });
      if (isAxiosError(error)) {
        toast.error(error.response?.data?.error || "Login failed");
        return;
      }
      toast.error("Login failed");
    },
    onSubmitSuccess: (response) => {
      if (!response) {
        toast.error("Login failed");
        return;
      }
      const {
        token,
        user,
        userDetails: uDetails,
        subscriptionStatus: subStatus,
      } = response.data;
      // Sentry.setUser({
      //   id: user.id,
      //   email: user.email,
      //   token,
      // });
      setUserDetails(uDetails);
      setSubscriptionStatus(subStatus);
      setCreds({ email: "", password: "" });
      return navigate("/people");
    },
  });

  const handleLogin = async (values: z.infer<typeof validator>) => {
    return axios.post<{
      token: string;
      user: User;
      userDetails: UserDetails;
      subscriptionStatus: SubscriptionStatus;
    }>("/auth/login", values);
  };

  return (
    <div className="min-h-[600px] w-full">
      <h1 className="mb-5 text-center text-4xl max-md:text-3xl">
        Attendance Made Easy
      </h1>
      <div className="flex flex-row flex-wrap justify-around gap-8 max-sm:flex-col-reverse max-sm:items-center">
        <div className="order-none mb-4 max-w-[650px] min-w-[320px] self-auto ps-4">
          <Welcome2 />
          <AppStoreBranding />
        </div>
        <div
          className="me-4 flex max-h-[350px] w-[320px] flex-initial flex-col items-center justify-start self-auto rounded-2xl border border-gray-500 p-3 dark:border-gray-500"
          id="login-form"
        >
          <p className="mb-2">
            Log In or{" "}
            <Link className="font-bold" to="/register">
              Sign Up
            </Link>
          </p>
          <form className="w-[275px]" {...form.getFormProps()}>
            <div className="flex flex-col gap-2">
              <RVFHiddenInput name="subaction" type="hidden" form={form} />
              <RVFInput name="email" label="Email" type="text" form={form} />
              <RVFInput
                name="password"
                label="Password"
                type="password"
                form={form}
              />

              <RVFSubmitButton form={form} />
            </div>
          </form>

          <Link to="/forgotpassword" className="text-xs">
            Forgot Password?
          </Link>
        </div>
      </div>
    </div>
  );
}
